import { convertFirstChar } from "@/services/generic.methods";

/**
 * Stable dictionary / form key for a custom field definition.
 * Prefers numeric FieldId; falls back to mappedName for legacy definitions.
 */
export const getFieldKey = (field) => {
  if (field?.id != null && field.id !== "") {
    return String(field.id);
  }
  if (field?.mappedName) {
    return field.mappedName;
  }
  return field?.name != null ? String(field.name) : "";
};

/** True when key is a Typed EAV FieldId string (e.g. "42"). */
export const isFieldIdKey = (key) => /^\d+$/.test(String(key ?? ""));

/** Legacy grid/settings column key derived from mappedName (e.g. CText1 → cText1). */
export const getLegacyColumnKey = (mappedName) => {
  if (!mappedName) {
    return null;
  }
  return convertFirstChar(mappedName);
};

/**
 * Resolve a custom field definition by FieldId, mappedName, name, or legacy camelCase slot.
 *
 * Intentional legacy fallbacks (remove after BE soak + backfill):
 * - mappedName match (CText1, etc.)
 * - camelCase slot key (cText1, etc.)
 */
export const resolveFieldByKey = (fields = [], key = "") => {
  if (!key || !fields?.length) {
    return undefined;
  }

  const keyStr = String(key);

  return fields.find((field) => {
    if (field?.id != null && String(field.id) === keyStr) {
      return true;
    }
    if (field?.name != null && String(field.name) === keyStr) {
      return true;
    }
    if (
      field?.mappedName &&
      String(field.mappedName).toLowerCase() === keyStr.toLowerCase()
    ) {
      return true;
    }
    const legacyKey = getLegacyColumnKey(field?.mappedName);
    if (legacyKey && legacyKey.toLowerCase() === keyStr.toLowerCase()) {
      return true;
    }
    return false;
  });
};

/** Match grid column metadata to a custom field (FieldId or legacy slot columnName). */
export const resolveFieldByColumnName = (fields = [], columnName = "") => {
  if (!columnName || !fields?.length) {
    return undefined;
  }
  return resolveFieldByKey(fields, columnName);
};

/** Grid column key for a custom field: numeric FieldId, or a loaded field's mapped slot. */
export const isCustomFieldColumnKey = (key, fields = []) =>
  isFieldIdKey(key) || !!resolveFieldByKey(fields, key);
